import { EpicIcons } from "@constellation-academy/epic-ui";
import * as Sentry from "@sentry/react";
import { memo, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import type { reenablePaywallButton_LearnOpportunityV2Fragment$key } from "@relay/reenablePaywallButton_LearnOpportunityV2Fragment.graphql";
import type { reenablePaywallButton_ReenablePaywallMutation } from "@relay/reenablePaywallButton_ReenablePaywallMutation.graphql";
import {
	LEARN_OPPORTUNITY_V2_FRAGMENT,
	REENABLE_PAYWALL_MUTATION,
} from "./reenable-paywall-button.graphql";
import type { ReenablePaywallButtonProps } from "./reenable-paywall-button.types";
import { Button, ButtonVariant } from "../../../../components/button";
import { ConfirmDialog } from "../../../../components/confirm-dialog";

const ReenablePaywallButtonComponent = ({
	learnOpportunityV2FragmentRef,
	userId,
	onCompleted,
	onError,
}: ReenablePaywallButtonProps) => {
	const { t } = useTranslation("common");
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const rootNode = useFragment<reenablePaywallButton_LearnOpportunityV2Fragment$key>(
		LEARN_OPPORTUNITY_V2_FRAGMENT,
		learnOpportunityV2FragmentRef ?? null,
	);

	const [reenablePaywall, isReenablingPaywall] =
		useMutation<reenablePaywallButton_ReenablePaywallMutation>(REENABLE_PAYWALL_MUTATION);

	const handleReenablePaywallOnClick = () => {
		setShowConfirmDialog(true);
	};

	const handleReenablePaywallOnCancel = () => {
		setShowConfirmDialog(false);
	};

	const handleReenablePaywallOnConfirm = () => {
		if (!rootNode) {
			setShowConfirmDialog(false);
			toast.error(t("userEdit.reenablePaywallRootNotFound"));
			Sentry.captureException(
				new Error("reenablePaywallButton_ReenablePaywallMutation: RootNode not found"),
				{
					extra: {
						userId,
						rootNode,
					},
				},
			);
			return;
		}

		reenablePaywall({
			variables: {
				input: { rootId: rootNode.id, userId },
			},
			onCompleted: () => {
				setShowConfirmDialog(false);
				toast.success(t("userEdit.reenablePaywallSuccess"));
				onCompleted?.();
			},
			onError: () => {
				setShowConfirmDialog(false);
				toast.error(t("userEdit.reenablePaywallError"));
				onError?.();
			},
		});
	};

	return (
		<>
			<Button
				label={t("userEdit.reenablePaywall")}
				icon={EpicIcons.BAN}
				variant={ButtonVariant.Warning}
				onClick={handleReenablePaywallOnClick}
				disabled={isReenablingPaywall}
				loading={isReenablingPaywall}
			/>
			<ConfirmDialog
				title={t("userEdit.reenablePaywallTitle")}
				content={t("userEdit.reenablePaywallConfirm", {
					name: rootNode?.structureDefinition.title ?? t("userEdit.unknownCourse"),
				})}
				confirmButtonText={t("userEdit.reenablePaywallConfirmButton")}
				onConfirm={handleReenablePaywallOnConfirm}
				onCancel={handleReenablePaywallOnCancel}
				showDialog={showConfirmDialog}
			/>
		</>
	);
};

export const ReenablePaywallButton = memo(ReenablePaywallButtonComponent);
